import React, { Fragment, useCallback, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { View, Radio, Text, Divider, Tag, Switch } from '@upstox/upstox-ui';
import Modal from 'app/components/MainLayout/components/Modal';
import { ModalFooter } from 'app/components/MainLayout/components/Modal/ModalFooter';
// constants
import {
  CloudStorage,
  LocalStorage,
  cloudStorageKeyPoints,
} from 'modules/TradingChart/consts';
import { EVENTS } from 'modules/Analytics/consts';
// services
import AnalyticsService from 'modules/Analytics/service';
import LocaleService from 'modules/Localization';
import UserService from 'modules/User';
import Settings from 'modules/Settings';
import AppStore from 'modules/App';
// typings
import { StorageOptions } from 'modules/ChartSync/typings';
// components
import { LearnMoreAboutChartSaveLink } from '../LearnMoreAboutChartSaveLink';
// styles
import * as css from './styles';

interface ChartSaveSettingsModalProps {
  onClose: () => void;
  handleSaveStorageOption: (theme: StorageOptions) => void;
  optedStorageOption?: StorageOptions;
  isSubmitting?: boolean;
}

export const ChartSaveSettingsModal = observer(
  ({
    onClose,
    handleSaveStorageOption,
    optedStorageOption = 'localStorage',
    isSubmitting,
  }: ChartSaveSettingsModalProps) => {
    const [selectedStorageOption, setSelectedStorageOption] = useState<
      StorageOptions
    >(optedStorageOption);
    const autoSaveCharts = Settings.getIsAutoSaveChartsEnabled();
    const [autoSaveChecked, setAutoSaveChecked] = useState(!!autoSaveCharts);

    const isAutoSaveValueChanged = () => autoSaveCharts !== autoSaveChecked;

    const isStorageOptionChanged = () => (
      selectedStorageOption === 'cloudStorage' &&
      optedStorageOption !== selectedStorageOption
    );

    const handleChange = useCallback(
      (option: StorageOptions) => {
        if (isSubmitting) return;
        setSelectedStorageOption(option);
      },
      [isSubmitting],
    );

    const handleAutoSaveToggle = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isSubmitting) return;
        const newValue = e.target.checked;
        setAutoSaveChecked(newValue);
      },
      [isSubmitting],
    );

    const handleConfirm = async () => {
      AnalyticsService.sendEvent(EVENTS.CHART_SAVE_SELECTED_OPTION, {
        preference: selectedStorageOption,
      }, true);
      const autoSaveChanged = isAutoSaveValueChanged();
      const storageOptionChanged = isStorageOptionChanged();
      // Save the preference
      if (autoSaveChanged) {
        await UserService.saveUserPreferenceSettings({
          autoSaveCharts: autoSaveChecked,
        });
      }
      // Proceed with uploading everything to cloud storage
      if (storageOptionChanged) {
        handleSaveStorageOption(selectedStorageOption);
      }
      // Toggle the modal only when the auto-save setting has changed,
      // but the storage option remains the same,
      // since handleSaveStorageOption manages its own flow & toggles the modal.
      if (autoSaveChanged && !storageOptionChanged) {
        AppStore.toggleModal('chartSaveSettings');
      }
    };

    const shouldDisableSubmitting = () => {
      if (!isAutoSaveValueChanged() && !isStorageOptionChanged()) {
        return true;
      }
      return false;
    };

    // Update state when props change
    useEffect(() => {
      setAutoSaveChecked(!!autoSaveCharts);
    }, [autoSaveCharts]);

    return (
      <Modal onClose={onClose} title="Save settings">
        {/* Invisible element to capture the initial focus */}
        <span style={{ height: 0, width: 0, position: 'absolute' }} autoFocus />
        <View as="div" gap="small" padding="medium" alignItems="center">
          <Text>Autosave charts</Text>
          <Switch
            checked={autoSaveChecked}
            onChange={handleAutoSaveToggle}
            style={{ outline: 'none' }}
          />
        </View>

        {!Settings.getIsChartSettingsSaveEnabled() && (
          <Fragment>
            <View as="div" gap="medium" padding="medium">
              <View>
                <Radio
                  name="storage-types"
                  value="cloudStorage"
                  id="cloudStorage"
                  checked={selectedStorageOption === 'cloudStorage'}
                  onChange={e =>
                    handleChange(e.currentTarget.value as StorageOptions)}
                />
              </View>
              <View flexDirection="column">
                <View alignItems="center" gap="medium">
                  <Text htmlFor={CloudStorage} as="label" family="tvFont">
                    {CloudStorage}
                  </Text>
                  <Tag
                    style={{ padding: '4px', margin: 0 }}
                    variant="green"
                    transform="capitalize"
                  >
                    {LocaleService.translate('RECOMMENDED')}
                  </Tag>
                </View>
                <View
                  margin={['large']}
                  css={css.listOptions}
                  as="ol"
                  flexDirection="column"
                >
                  {cloudStorageKeyPoints.map(item => (
                    <Text key={item} as="li" color="text.2">
                      {item}
                    </Text>
                  ))}
                </View>

                <LearnMoreAboutChartSaveLink />
              </View>
            </View>

            <View as="div" gap="medium" padding="medium">
              <View>
                <Radio
                  name="storage-types"
                  value="localStorage"
                  id="localStorage"
                  checked={selectedStorageOption === 'localStorage'}
                  onChange={e =>
                    handleChange(e.currentTarget.value as StorageOptions)}
                />
              </View>
              <View flexDirection="column">
                <Text htmlFor={LocalStorage} as="label" family="tvFont">
                  {LocalStorage}
                </Text>
                <Text color="text.2">
                  {LocaleService.translate('LOCAL_STORAGE_DATA')}
                </Text>
              </View>
            </View>

            <Divider />
          </Fragment>
        )}

        <ModalFooter
          onClose={onClose}
          disabledSubmitting={shouldDisableSubmitting()}
          confirmActionTitle={LocaleService.translate('BUTTON_CONFIRM')}
          isSubmitting={isSubmitting}
          onConfirm={handleConfirm}
        />
      </Modal>
    );
  },
);
